import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ComponentProps,
  type ReactNode,
} from 'react';

/** Gemeinsame Bausteine der Oberfläche. Module nutzen diese, damit alles gleich aussieht. */

export function Knopf({
  art = 'normal',
  klein,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { art?: 'normal' | 'haupt' | 'gefahr' | 'leise'; klein?: boolean }) {
  return <button type="button" className={`knopf knopf-${art}${klein ? ' knopf-klein' : ''} ${className}`} {...rest} />;
}

export function Feld({ titel, hinweis, children }: { titel: string; hinweis?: ReactNode; children: ReactNode }) {
  return (
    <label className="feld">
      <span className="feld-titel">{titel}</span>
      {children}
      {hinweis && <span className="feld-hinweis">{hinweis}</span>}
    </label>
  );
}

// ref wird durchgereicht (React 19): <Eingabe ref={…} />
export const Eingabe = ({ className = '', ...p }: ComponentProps<'input'>) => <input className={`eingabe ${className}`} {...p} />;
export const Textfeld = ({ className = '', ...p }: ComponentProps<'textarea'>) => <textarea className={`eingabe ${className}`} {...p} />;
export const Auswahl = ({ className = '', ...p }: ComponentProps<'select'>) => <select className={`eingabe ${className}`} {...p} />;

export function Meldung({ art = 'fehler', children }: { art?: 'fehler' | 'ok' | 'info'; children: ReactNode }) {
  if (!children) return null;
  return <div className={`meldung meldung-${art}`}>{children}</div>;
}

export function Laden({ text = 'Lädt …' }: { text?: string }) {
  return (
    <div className="laden">
      <span className="kreisel" aria-hidden /> {text}
    </div>
  );
}

export function Leer({ titel, children }: { titel: string; children?: ReactNode }) {
  return (
    <div className="leer">
      <strong>{titel}</strong>
      {children && <div>{children}</div>}
    </div>
  );
}

export function Karte({ titel, aktion, children, className = '' }: { titel?: ReactNode; aktion?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`karte ${className}`}>
      {(titel || aktion) && (
        <header className="karte-kopf">
          {titel && <h3>{titel}</h3>}
          {aktion}
        </header>
      )}
      {children}
    </section>
  );
}

/** Dialog (modal). Schließt mit Escape oder Klick daneben. */
export function Dialog({
  titel,
  offen,
  schliessen,
  children,
  fuss,
  breit,
}: {
  titel: string;
  offen: boolean;
  schliessen(): void;
  children: ReactNode;
  fuss?: ReactNode;
  breit?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (offen && !d.open) {
      d.showModal();
      // Fokus auf das erste Eingabefeld (sonst landet er auf „Schließen“ und Enter schließt den Dialog)
      requestAnimationFrame(() => {
        const ziel =
          d.querySelector<HTMLElement>('.dialog-inhalt [autofocus]') ??
          d.querySelector<HTMLElement>('.dialog-inhalt input:not([disabled]):not([type=hidden]), .dialog-inhalt textarea:not([disabled]), .dialog-inhalt select:not([disabled])');
        ziel?.focus();
      });
    }
    if (!offen && d.open) d.close();
  }, [offen]);
  return (
    <dialog
      ref={ref}
      className={`dialog${breit ? ' dialog-breit' : ''}`}
      onClose={schliessen}
      onClick={(e) => e.target === ref.current && schliessen()}
    >
      {offen && (
        <div className="dialog-innen">
          <header className="dialog-kopf">
            <h2>{titel}</h2>
            <button type="button" className="knopf knopf-leise" aria-label="Schließen" onClick={schliessen}>
              ✕
            </button>
          </header>
          <div className="dialog-inhalt">{children}</div>
          {fuss && <footer className="dialog-fuss">{fuss}</footer>}
        </div>
      )}
    </dialog>
  );
}

/** Lädt Daten und bietet neuLaden(); Fehler als Text. */
export function useDaten<T>(laden: () => Promise<T>, abh: unknown[] = []) {
  const [daten, setDaten] = useState<T | null>(null);
  const [fehler, setFehler] = useState('');
  const [laedt, setLaedt] = useState(true);
  const ref = useRef(laden);
  ref.current = laden;
  const neuLaden = useCallback(async () => {
    setLaedt(true);
    try {
      setDaten(await ref.current());
      setFehler('');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setLaedt(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, abh);
  useEffect(() => {
    void neuLaden();
  }, [neuLaden]);
  return { daten, setDaten, fehler, laedt, neuLaden };
}

/** Führt eine Aktion aus und merkt sich Fehler/Läuft-Zustand – für Knöpfe und Formulare. */
export function useAktion() {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState('');
  const ausfuehren = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setLaeuft(true);
    setFehler('');
    try {
      return await fn();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Fehler');
      return undefined;
    } finally {
      setLaeuft(false);
    }
  }, []);
  return { laeuft, fehler, setFehler, ausfuehren };
}

export function datumZeit(iso: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
}

export function datum(iso: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('de-DE', { dateStyle: 'medium' });
}

export function groesse(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}
